import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { IconTrash as DeleteIcon, IconSearch } from '@tabler/icons-react';
import { api } from '../services/api';
import payeeCacheService from '../services/payeeCache';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useToast } from '../components/ToastNotification';
import { useCanEdit } from '../components/RoleGate';
import { CenteredLoader } from '../components/LoadingIndicator';
import { PageHero, SectionContainer, Row, Segmented, EntryTypeHelp, Badge, Switch } from '../components/ds';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { entryTypeAbbrOptions, interpolate } from '../utils/entryType';

const errMessage = (err) =>
  err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

const PayeesCleanup = ({ isAuthReady = false }) => {
  const { activeGroupId, loading: groupsLoading } = useGroup();
  const canEdit = useCanEdit();
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  // v4 board (#1377): wide desktop lays payees in two balanced columns;
  // mobile gets a filter box above the list.
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Default Entry Type per row (#1938): S / I / T / OT, full word on hover.
  const strings = useEngineStrings();
  // Letters carry the type colors, as the chips do (#1938).
  const typeOptions = useMemo(() => entryTypeAbbrOptions(strings, theme.palette.entryType), [strings, theme.palette.entryType]);

  const [payees, setPayees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!activeGroupId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(activeGroupId, '/payees');
      setPayees(res?.payees || []);
    } catch (err) {
      setError(errMessage(err));
      setPayees([]);
    } finally {
      setLoading(false);
    }
  }, [activeGroupId]);

  useEffect(() => {
    if (isAuthReady && activeGroupId && canEdit) load();
  }, [isAuthReady, activeGroupId, canEdit, load]);

  const runDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    const deletedId = confirm.payeeId;
    const deletedName = confirm.name;
    try {
      await api.delete(activeGroupId, `/payees/${encodeURIComponent(deletedId)}`);
      setPayees(prev => prev ? prev.filter(p => p.payeeId !== deletedId) : prev);
      await payeeCacheService.invalidatePayeeCache('payee_deleted');
      showSuccess(`Deleted "${deletedName}"`);
      setConfirm(null);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // Write the row's default (#1938): PUT, keep the row, flush the payee cache
  // so the detail pages read the new default instead of a 30-minute-old copy.
  const setDefault = async (payee, defaultEntryType) => {
    try {
      const res = await api.put(activeGroupId, `/payees/${encodeURIComponent(payee.payeeId)}`, { defaultEntryType });
      const updated = { ...payee, ...(res?.payee || {}), defaultEntryType };
      setPayees(prev => prev ? prev.map(p => (p.payeeId === payee.payeeId ? { ...p, ...updated } : p)) : prev);
      await payeeCacheService.invalidatePayeeCache('default_changed');
    } catch (err) {
      showError(errMessage(err));
    }
  };

  // The Paycheck switch (#2288): on marks the payee as a source of income
  // (the API sets its Income default in the same write), off removes the
  // mark. Stamped lines stay as entered either way.
  const setProvider = async (payee, on) => {
    try {
      const res = await api.put(activeGroupId, `/payees/${encodeURIComponent(payee.payeeId)}`, { incomeProvider: on ? true : null });
      const row = { ...payee, ...(res?.payee || {}) };
      if (on) {
        row.incomeProvider = true;
        row.defaultEntryType = 'income';
      } else {
        delete row.incomeProvider;
      }
      setPayees(prev => prev ? prev.map(p => (p.payeeId === payee.payeeId ? row : p)) : prev);
      await payeeCacheService.invalidatePayeeCache('default_changed');
    } catch (err) {
      showError(errMessage(err));
    }
  };

  if (!isAuthReady || groupsLoading) return <CenteredLoader />;

  if (!canEdit) {
    return (
      <Box sx={{ pb: 8 }}>
        <PageHero label="Manage payees" />
        <Box sx={{ px: 1, mt: 2 }}>
          <Alert severity="warning">
            You need editor or owner role in this group to manage payees.
          </Alert>
        </Box>
      </Box>
    );
  }

  const count = payees?.length ?? 0;
  const visiblePayees = (payees || []).filter(
    (p) => !filter.trim() || p.name.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <Box sx={{ pb: 8 }}>
      {/* v4 board (#1377): title + quiet count aux; the payee total lives in
          the section header count slot. */}
      <PageHero
        label="Manage payees"
        aux={
          <Typography component="div" sx={{ fontSize: '14px', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {`${count.toLocaleString('en-US')} ${count === 1 ? 'payee' : 'payees'}`}
          </Typography>
        }
      />

      <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Deleting a payee removes it from your dropdowns going forward. Existing
          transactions keep the payee name they were saved with - they are not
          reassigned. To change the payee on a past transaction, open that
          transaction in its ledger and edit the payee field.
        </Alert>

        {/* v4 board: mobile filter box above the list. */}
        {isMobile && count > 0 && (
          <TextField
            size="small"
            fullWidth
            placeholder="Filter payees..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ mb: '13px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch size={15} aria-hidden />
                </InputAdornment>
              ),
            }}
          />
        )}

        {loading && <CenteredLoader />}
        {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && payees && (
          <SectionContainer
            title="Payees"
            count={count.toLocaleString('en-US')}
            category="neutral"
            density={isMobile ? 'compact' : 'default'}
            note={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {strings['payees.defaultNote']}
                <EntryTypeHelp />
              </Box>
            }
          >
            {count === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>
                No payees yet.
              </Typography>
            ) : (
              <Box
                sx={{
                  // v4 board: wide desktop = two balanced columns, 44px gap.
                  display: { xs: 'block', md: 'grid' },
                  gridAutoFlow: 'column',
                  gridTemplateRows: `repeat(${Math.max(1, Math.ceil(visiblePayees.length / 2))}, auto)`,
                  columnGap: '44px',
                }}
              >
                {visiblePayees.map((p, idx) => (
                  <Row
                    key={p.payeeId}
                    name={p.linkedLedgerId || p.incomeProvider ? (
                      // A ledger's own payee (#1944): locked to Transfer and
                      // undeletable here; the badge says why. An income
                      // provider (#2288) carries the Paycheck badge.
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</Box>
                        {p.linkedLedgerId ? (
                          <Badge variant="neutral" data-testid="payee-ledger-badge">{strings['payees.ledgerBadge']}</Badge>
                        ) : (
                          <Badge variant="neutral" data-testid="payee-paycheck-badge">{strings['payees.paycheckBadge']}</Badge>
                        )}
                      </Box>
                    ) : p.name}
                    size={isWide ? 'compact' : 'default'}
                    // Convention 7: the trash is ALWAYS visible - quiet grey,
                    // turning negative on hover; the row itself never
                    // highlights (trailing, not hover-revealed actions).
                    trailing={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Segmented
                          ariaLabel={interpolate(strings['payees.defaultAria'], { payee: p.name })}
                          options={typeOptions}
                          value={p.defaultEntryType || null}
                          onChange={(v) => setDefault(p, v)}
                          disabled={Boolean(p.linkedLedgerId)}
                        />
                        {/* The Paycheck switch (#2288), label then control; a
                            ledger's payee never has one. */}
                        {!p.linkedLedgerId ? (
                          <Box
                            component="span"
                            data-testid="payee-paycheck-switch"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap' }}
                          >
                            {strings['payees.paycheckBadge']}
                            <Switch
                              checked={Boolean(p.incomeProvider)}
                              onChange={(next) => setProvider(p, next)}
                              ariaLabel={interpolate(strings['payees.paycheckAria'], { payee: p.name })}
                            />
                          </Box>
                        ) : null}
                        <IconButton
                          size="small"
                          aria-label={`Delete payee ${p.name}`}
                          onClick={() => setConfirm(p)}
                          disabled={deleting || Boolean(p.linkedLedgerId)}
                          sx={(t) => ({
                            color: t.palette.text.dim,
                            padding: '2px',
                            transition: `color ${t.ds.motion.instant} ${t.ds.motion.easeOut}`,
                            '&:hover': { color: t.palette.error.main, backgroundColor: 'transparent' },
                          })}
                        >
                          <DeleteIcon size={15} />
                        </IconButton>
                      </Box>
                    }
                    last={idx === visiblePayees.length - 1}
                  />
                ))}
              </Box>
            )}
            {count > 0 && (
              <Typography component="div" sx={{ fontSize: 12, color: 'text.secondary', pt: '10px' }}>
                <div>{strings['payees.defaultFooter']}</div>
                <div>{strings['payees.paycheckNote']}</div>
              </Typography>
            )}
          </SectionContainer>
        )}
      </Box>

      <Dialog open={!!confirm} onClose={() => !deleting && setConfirm(null)}>
        <DialogTitle>Delete payee?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove "{confirm?.name}" from your dropdowns? Past transactions will
            keep this name on their lines.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={runDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayeesCleanup;
